/**
 * `/sales/invoices/<id>/edit` — composer pre-loaded with an existing draft.
 *
 * Same orchestrator as `/sales/invoices/new`; the route just passes the
 * URL's `invoiceId` as `loadDraftId`. The composer hydrates state
 * from `invoices.get`, then autosaves via `updateDraft`. If the row
 * isn't a draft, the composer redirects to the read-only detail
 * page (since you can't edit issued documents).
 *
 * @see 11-new-invoice-screen-spec.md §6 — drafts are first-class.
 * @see 08-system-decisions.md §3 — issued invoices are append-only.
 */
import { createFileRoute, notFound } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { hasPermission } from "@flint/api-contract";
import { Alert } from "@flint/ui";
import { WorkspaceShell } from "../../../features/workspace/index.ts";
import { ComposerPage } from "../../../features/invoices/index.ts";
import { meQueryOptions } from "../../../lib/session.ts";

export const Route = createFileRoute("/(workspace)/sales/invoices_/$invoiceId/edit")({
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQueryOptions).catch(() => null);
    const perms = me?.company?.permissions ?? [];
    if (!hasPermission(perms, "invoices.create")) {
      throw notFound();
    }
  },
  component: EditInvoiceRoute,
});

function EditInvoiceRoute() {
  const { invoiceId } = Route.useParams();
  const me = useQuery(meQueryOptions);
  const canCreate = hasPermission(me.data?.company?.permissions ?? [], "invoices.create");

  return (
    <WorkspaceShell
      breadcrumbs={[
        { label: "Sales" },
        { label: "Invoices", to: "/sales/invoices" },
        { label: "Edit draft" },
      ]}
      viewportClassName="!p-0"
      contentClassName="max-w-none"
    >
      {me.isLoading ? (
        <div className="p-6 text-sm text-muted">Checking invoice permissions...</div>
      ) : canCreate ? (
        <ComposerPage loadDraftId={invoiceId} />
      ) : (
        <div className="mx-auto max-w-3xl p-6">
          <Alert
            intent="bad"
            title="You don't have permission to edit invoice drafts"
            description="Ask an Owner or Admin to grant invoice draft access."
          />
        </div>
      )}
    </WorkspaceShell>
  );
}
